import { Page } from 'react-pdf';
import styled, { css } from 'styled-components';
import { colors } from '@ch/common/const/color';
import { BoxStyled } from '@ch/common/components/box';
import { ButtonStyled } from '@ch/common/components/button';
import { FormErrorStyled } from '@ch/common/components/form/error';
import { IconStyled } from '@ch/common/components/icon';
import { AnnotationField } from './types';
import { GridBreakpointsMediaDown, GridBreakpointsMediaUp } from '@ch/common/const/grid';
import { Link } from '@ch/common/components/link';
import { Container } from 'styled-bootstrap-grid';
import { Text } from '@ch/common/components/text';

export const QuizWrap = styled.div`
  position: relative;
  padding: 0 0 20px;

  ${GridBreakpointsMediaDown.lg} {
    padding: 0 0 20px;
  }
`;

export const QuizInner = styled.div<{ fullWidth?: boolean }>`
  min-height: calc(100vh - 247px);
  display: ${({ fullWidth }) => (fullWidth ? 'flex' : 'block')};
  align-items: center;
  justify-content: center;

  @media print {
    .print-media-container {
      min-width: 100%;
    }
  }
`;

export const QuizGroupNavItemStyled = styled.li<{ $width: number; $active: boolean; $completed: boolean }>`
  width: ${({ $width }) => `${$width}%`};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  &:before {
    position: absolute;
    z-index: 0;
    top: 14px;
    border-top: 2px dashed ${colors.grayLight};
    content: '';
    ${GridBreakpointsMediaDown.lg} {
      top: 10px;
    }
  }
  &:before {
    left: -48%;
    right: 50%;
  }
`;

export const QuizGroupNavLink = styled.span`
  cursor: pointer;
  border: none;
  background: transparent;
  position: relative;
  padding: 26px 0 0;
  text-decoration: none;
  &:disabled {
    cursor: default;
  }
  ${GridBreakpointsMediaDown.lg} {
    padding: 22px 0 0;
  }
`;

export const QuizGroupNavStyled = styled.ul`
  width: 100%;
  display: flex;
  list-style: none;
  margin: 0;
  padding: 15px 0 15px;
  align-items: start;
  ${QuizGroupNavItemStyled} {
    &:nth-child(1):before {
      display: none;
    }
    &:nth-last-child(1):after {
      display: none;
    }
    &:first-child,
    &:last-child {
      width: 9%;
    }
    &:nth-child(2) {
      width: 12%;
    }
  }
`;

export const QuizGroupNavItemIcon = styled.span`
  display: block;
  padding: 0 5px;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 50%;
  margin: 0 0 0 -20px;
  background: ${colors.white};
  ${GridBreakpointsMediaDown.lg} {
    height: 22px;
    margin: 0 0 0 -16px;
  }
  height: 32px;
`;

export const QuizGroupNavItemCompleted = styled(BoxStyled)`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  position: absolute;
  bottom: 1px;
  right: 4px;

  ${GridBreakpointsMediaDown.lg} {
    width: 9px;
    height: 9px;
    right: 5px;
  }
`;

export const QuizFieldPersonStyled = styled(BoxStyled)`
  position: relative;
  ${FormErrorStyled} {
    width: 100%;
    position: absolute;
    display: block;
    top: 100%;
    text-align: center;
  }
`;

export const QuizPersonBlock = styled(BoxStyled)<{ $error?: boolean }>`
  min-height: 350px;
  border: 1px solid ${({ $error }) => ($error ? colors.error : colors.primaryLighter)};
  border-radius: 8px;
  cursor: pointer;
`;

export const QuizPersonBlockImg = styled.img<{ $disabled?: boolean }>`
  width: 100%;
  height: auto;
  display: block;
  filter: ${({ $disabled }) => ($disabled ? 'grayscale(100%)' : 'inherit')};
`;

export const QuizDialogDocumentWrap = styled.div`
  position: relative;
  display: table;
  margin: 0 auto;
  .react-pdf__Page__canvas {
  }
  .react-pdf__Page__annotations.annotationLayer {
    display: none;
  }
`;

const quizDialogAnnotationDefault = css`
  position: absolute;
  font-size: 10px;
`;

const textStyles = ({ $width, $height, $top, $left }: AnnotationField) => css`
  width: ${$width - 3}px;
  height: ${$height}px;
  top: ${$top - 2}px;
  left: ${$left}px;
  border: none;
  background: whitesmoke;
  ${quizDialogAnnotationDefault}
`;
const inputStyles = ({ $width, $height, $top, $left }: AnnotationField) => css`
  width: ${$width}px;
  height: ${$height}px;
  top: ${$top - 2}px;
  left: ${$left}px;
  ${quizDialogAnnotationDefault}
`;

export const QuizDialogAnnotationText = styled.input<{ [key: string]: any }>`
  ${({ $width, $height, $top, $left }) => textStyles({ $width, $height, $top, $left })}
`;
export const QuizDialogAnnotationTextArea = styled.textarea<{ [key: string]: any }>`
  resize: none;
  ${({ $width, $height, $top, $left }) => textStyles({ $width, $height, $top, $left })}
`;
export const QuizDialogAnnotationRadio = styled.input<{ [key: string]: any }>`
  ${({ $width, $height, $top, $left }) => inputStyles({ $width, $height, $top, $left })}
`;
export const QuizDialogAnnotationCheckbox = styled.input<{ [key: string]: any }>`
  ${({ $width, $height, $top, $left }) => inputStyles({ $width, $height, $top, $left })}
`;
export const QuizDialogAnnotationSelect = styled.select<{ [key: string]: any }>`
  width: ${({ $width }) => `${$width + 5}px`};
  height: ${({ $height }) => `${$height}px`};
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left - 4}px`};
  ${quizDialogAnnotationDefault}
`;

export const QuizFieldDuplicatorStyled = styled(BoxStyled)`
  position: relative;
  border: 1px solid;
  border-radius: 10px;
  border-color: ${colors.grayDarker};
  > ${IconStyled} {
    position: absolute;
    top: 7px;
    right: 15px;
    cursor: pointer;
  }
  > div > label:first-child {
    padding-right: 30px;
  }
`;
export const QuizComplexFieldBlockStyled = styled(BoxStyled)`
  position: relative;
`;
export const QuizComplexFieldDelete = styled(ButtonStyled)`
  position: absolute;
  top: 8px;
  right: 25px;
`;

export const PageStyled = styled(Page as any)`
  display: ${({ $active }) => ($active ? 'block' : 'none')};
`;

export const CaptchaWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 15px;
  position: relative;
  ${FormErrorStyled}, .invalid-feedback {
    padding-top: 5px;
  }
`;
export const QuizConfirmationImg = styled.img`
  width: 30%;
  height: auto;
`;

export const QuizNavWrap = styled.div`
  background: ${colors.white};
  box-shadow: 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 2px 4px rgba(0, 0, 0, 0.2);
  border-top: 1px solid ${colors.primary};
  margin-bottom: 45px;
  position: sticky;
  left: 0;
  top: 0;
  z-index: 35;

  @media print {
    position: relative;
  }
`;

export const QuizSectionHidden = styled.div`
  position: absolute;
  left: 0%;
  top: 0%;
  right: auto;
  bottom: auto;
  margin-top: -101px;
  ${GridBreakpointsMediaDown.lg} {
    margin-top: -52px;
  }
`;

export const QuizQuestionWrapper = styled.div`
  position: relative;
`;

export const QuizPersonaWrapper = styled.div`
  background: ${colors?.lightBlue};
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 30px 0 15px 0;
  position: relative;
  width: 100vw;
`;

export const QuizCaptchaLoaderWrapper = styled.div`
  position: relative;
  height: 78px;
  width: 24px;
  z-index: 20;
`;

export const QuizStyledLink = styled(Link)`
  color: ${colors?.tertiaryBlue};
  &:hover {
    text-decoration: underline;
  }
`;

export const QuizNavContainer = styled(Container)`
  ${GridBreakpointsMediaUp.lg} {
    max-width: 100%;
  }
`;

export const QuizComplexFieldWrapper = styled('div')`
  display: flex;
  justify-content: start;
  align-items: center;
  overflow: hidden;
`;

export const QuizComplexFieldText = styled(Text)`
  text-overflow: ellipsis;
  white-space: pre;
  overflow: hidden;
  width: 100%;
`;
